<template>
  <div class="content">
    <v-row no-gutters>
      <v-col cols="6">
        <h1 class="title">{{ $t("salesStatistics.title") }}</h1>
      </v-col>
      <v-col cols="6" align="end">
        <v-btn
          href="/files/order_summary_export.xls"
          :download="`order_summary_export-${currentDateString}.xls`"
          color="warning"
          >{{ $t("salesStatistics.button.export") }}</v-btn
        >
      </v-col>
    </v-row>
    <div class="table">
      <div class="inputs">
        <v-row>
          <v-col
            style="max-height: 60px"
            cols="12"
            xs="12"
            lg="3"
            md="4"
            sm="6"
          >
            <div class="flatpickr">
              <label
                for="sales_date"
                :class="
                  labelForSalesFlatpickr
                    ? 'flatpickrLabel'
                    : 'flatpickrHiddenLabel'
                "
              >
                {{ $t("salesStatistics.label.salesDate") }}</label
              >
              <div class="date-picker">
                <flat-pickr
                  v-model="sales_date"
                  :config="salesDateConfig"
                  name="sales_date"
                ></flat-pickr>
              </div>
            </div>
          </v-col>
          <v-col
            style="max-height: 60px"
            cols="12"
            xs="12"
            lg="3"
            md="4"
            sm="6"
          >
            <div class="flatpickr">
              <label
                for="expire_date"
                :class="
                  labelForExpireFlatpickr
                    ? 'flatpickrLabel'
                    : 'flatpickrHiddenLabel'
                "
                >{{ $t("salesStatistics.label.expireDate") }}</label
              >
              <div class="date-picker">
                <flat-pickr
                  v-model="expire_date"
                  :config="expireDateConfig"
                  name="expire_date"
                ></flat-pickr>
              </div>
            </div>
          </v-col>
          <v-col
            style="max-height: 60px"
            cols="12"
            xs="12"
            lg="3"
            md="4"
            sm="6"
          >
            <v-select
              :label="$t('salesStatistics.label.usage')"
              v-model="selectedUsage"
              :items="usageValues"
              item-text="title"
              item-value="value"
              clearable
              @keydown.enter="handleFilter(false, true)"
              :readonly="enterClicked ? true : false"
            ></v-select>
          </v-col>
          <v-col
            style="max-height: 60px"
            cols="12"
            xs="12"
            lg="3"
            md="4"
            sm="6"
            v-if="isSuperAdmin"
          >
            <v-select
              :label="$t('salesStatistics.label.partner')"
              v-model="selectedPartner"
              :items="partnersWithId"
              item-title="name"
              item-value="id"
              clearable
              @keydown.enter="handleFilter(false, true)"
              :readonly="enterClicked ? true : false"
            ></v-select>
          </v-col>
          <v-col cols="12" xs="12" lg="3" md="4" sm="6">
            <v-select
              :label="$t('salesStatistics.label.product')"
              v-model="selectedProduct"
              :items="products"
              item-title="name"
              item-value="id"
              clearable
              @keydown.enter="handleFilter(false, true)"
              :readonly="enterClicked ? true : false"
            ></v-select>
          </v-col>
          <v-col cols="12" xs="12" lg="3" md="4" sm="6">
            <v-select
              :label="$t('salesStatistics.label.providerPlaceholder')"
              v-model="payment_provider"
              :items="providerTypes"
              clearable
              @keydown.enter="handleFilter(false, true)"
              :readonly="enterClicked ? true : false"
            ></v-select>
          </v-col>
        </v-row>
        <v-btn class="mt-0" @click="handleFilter(false)">{{
          $t("salesStatistics.button.filter")
        }}</v-btn>
      </div>
      <div>
        <v-data-table
          v-if="!isLoading"
          v-model:page="page"
          :headers="headers"
          :items="list"
          :items-per-page="limit"
          hide-default-footer
          class="elevation-0"
        >
          <template v-slot:item="{ item }">
            <tr>
              <!-- <td>{{ item.columns.product_group_id }}</td> -->
              <td>{{ item.columns.product_group_name }}</td>
              <td>{{ item.columns.par_name }}</td>
              <!-- <td>{{ item.columns.product_id }}</td> -->
              <td>{{ item.columns.product_name }}</td>
              <td>
                {{
                  parseInt(item.columns.price_gross).toLocaleString("hu", {
                    style: "currency",
                    currency: "HUF",
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })
                }}
              </td>
              <td>{{ item.columns.count }}</td>
              <td>
                {{
                  parseInt(item.columns.price_sum).toLocaleString("hu", {
                    style: "currency",
                    currency: "HUF",
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })
                }}
              </td>
            </tr>
          </template>
          <template v-slot:no-data>
            <Vue3Lottie
              :animationData="NoDataLottie"
              :height="200"
              :width="200"
            />
            <div class="pb-5 table-no-data">
              <h3>{{ $t("tableNoDataTitle") }}</h3>
              <p>{{ $t("tableNoDataDesc") }}</p>
            </div>
          </template>
          <template v-slot:bottom>
            <div class="botSide">
              <v-row
                align="center"
                justify-sm="end"
                justify="center"
                class="px-2"
              >
                <div class="page-sum" v-if="pageSum">
                  {{ $t("salesStatistics.pageSum") }}
                  {{
                    parseInt(pageSum).toLocaleString("hu", {
                      style: "currency",
                      currency: "HUF",
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })
                  }}
                </div>
                <v-col cols="12" xs="12" lg="3" md="4" sm="6">
                  <v-row align="center" justify="end"
                    ><div class="pr-2 pageSelectorLabel">
                      {{ $t("salesStatistics.tableFooterText") }}
                    </div>
                    <v-select
                      v-model="limit"
                      hide-details
                      @update:model-value="handleFilter(false)"
                      :items="pageSizes"
                      class="pageSelector pl-5"
                    ></v-select>
                    <div class="pl-2">
                      {{ displayRange }}
                    </div>
                  </v-row>
                </v-col>
                <v-col
                  cols="12"
                  xs="12"
                  lg="3"
                  md="4"
                  sm="5"
                  class="plrde-1 pr-1 pagination"
                >
                  <v-pagination
                    v-model="page"
                    :length="pageCount"
                    rounded="circle"
                    :total-visible="1"
                    show-first-last-page="true"
                    @update:model-value="handleFilter(true)"
                  ></v-pagination>
                </v-col>
              </v-row>
            </div>
          </template>
        </v-data-table>
      </div>
      <v-card v-if="isLoading" class="loading">
        <v-progress-circular
          v-if="isLoading"
          size="73.5"
          indeterminate
          class="loading-circle"
        ></v-progress-circular>
      </v-card>
    </div>
  </div>
</template>

<script>
import { Vue3Lottie } from "vue3-lottie";
import NoDataLottie from "@/assets/lotties/nodatalottie.json";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import { mapGetters, mapActions } from "vuex";
import moment from "moment";
import { Hungarian } from "flatpickr/dist/l10n/hu.js";
import { PAYMENT_PROVIDER } from "../../../components/enums";

export default {
  name: "SalesStatistics",
  props: {},
  components: {
    flatPickr,
    Vue3Lottie,
  },
  data() {
    return {
      currentDateString: this.getCurrentDateString(),
      NoDataLottie,
      headers: [
        // {
        //   title: this.$t("salesStatistics.table.headers.product_group_id"),
        //   key: "product_group_id",
        //   align: "start",
        //   sortable: false,
        // },
        {
          title: this.$t("salesStatistics.table.headers.product_group"),
          key: "product_group_name",
          sortable: false,
        },
        {
          title: this.$t("salesStatistics.table.headers.partner"),
          key: "par_name",
          sortable: false,
        },
        // {
        //   title: this.$t("salesStatistics.table.headers.product_id"),
        //   key: "product_id",
        //   sortable: false,
        // },
        {
          title: this.$t("salesStatistics.table.headers.product_title"),
          key: "product_name",
          sortable: false,
        },
        {
          title: this.$t("salesStatistics.table.headers.price"),
          key: "price_gross",
          sortable: false,
        },
        {
          title: this.$t("salesStatistics.table.headers.count"),
          key: "count",
          sortable: false,
        },
        {
          title: this.$t("salesStatistics.table.headers.sum"),
          key: "price_sum",
          sortable: false,
        },
      ],

      hiddenSalesDateLabel: false,
      hiddenExpireDateLabel: false,

      //Pagination settings
      page: undefined,
      limit: undefined,
      total: undefined,
      pageSizes: [10, 25, 50, 100],
      pageSum: undefined,

      //For Search
      sales_date: "",
      expire_date: "",
      payment_provider: undefined,

      providerTypes: [
        this.$t(`orders.table.payment_providers.${PAYMENT_PROVIDER.SIMPLE}`),
        this.$t(`orders.table.payment_providers.${PAYMENT_PROVIDER.SZEP_CARD}`),
      ],

      // For Flatpicker
      salesDateConfig: {
        altFormat: "Y-m-d",
        altInput: true,
        dateFormat: "Y-m-d",
        allowInput: false,
        enableTime: false,
        clear: true,
        mode: "range",
        disableMobile: true,
        locale: {
          ...Hungarian,
          rangeSeparator: " ",
        },
        onOpen: () => {
          this.hiddenSalesDateLabel = true;
        },
        onClose: () => {
          this.hiddenSalesDateLabel = false;
        },
        onKeyDown: (selectedDates, dateStr, instance, event) => {
          if (event.key === "Enter") {
            instance.close();
            this.handleFilter(false);
          }
        },
      },

      expireDateConfig: {
        altFormat: "Y-m-d",
        altInput: true,
        dateFormat: "Y-m-d",
        allowInput: false,
        enableTime: false,
        clear: true,
        mode: "range",
        disableMobile: true,
        locale: {
          ...Hungarian,
          rangeSeparator: " ",
        },
        onOpen: () => {
          this.hiddenExpireDateLabel = true;
        },
        onClose: () => {
          this.hiddenExpireDateLabel = false;
        },
        onKeyDown: (selectedDates, dateStr, instance, event) => {
          if (event.key === "Enter") {
            instance.close();
            this.handleFilter(false);
          }
        },
      },

      usageValues: [
        {
          value: true,
          title: this.$t("salesStatistics.usageFilter.used"),
        },
        {
          value: false,
          title: this.$t("salesStatistics.usageFilter.notUsed"),
        },
      ],

      selectedUsage: undefined,

      isLoading: false,
      error: false,

      list: [],

      selectedPartner: undefined,
      partnersWithId: [],

      products: [],
      selectedProduct: undefined,

      enterClicked: false,
    };
  },
  created() {
    this.getPartnerList();
    this.getProductList();

    this.getSalesStatistic();
  },

  methods: {
    ...mapActions({
      getPartners: "users/getPartners",
      exportTable: "statistics/exportTable",
      getSalesStatisticApi: "statistics/getSalesStatistic",
      productList: "products/productList",
    }),

    getCurrentDateString() {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");

      return `${year}_${month}_${day}`;
    },

    getPartnerList() {
      if (this.isSuperAdmin) {
        this.getPartners()
          .then((response) => {
            this.partnersWithId = response.result.map((partner) => {
              return { id: partner.id, name: partner.par_name };
            });
          })
          .catch((err) => {
            console.log(err);
          });
      }
    },

    getProductList() {
      this.productList({ is_paginated: false })
        .then((response) => {
          this.products = response.list.map((product) => {
            return { id: product.id, name: product.name };
          });
        })
        .catch((err) => {
          console.log(err);
        });
    },

    getSalesStatistic() {
      this.getSalesStatisticApi()
        .then((res) => {
          this.limit = res.limit;
          this.total = res.total;
          this.page = res.page;
          this.list = res.list;
          this.pageSum = res.sum;
          this.isLoading = false;
        })
        .catch((err) => {
          console.log(err);
        });
    },

    formatDate(dateString) {
      return moment(dateString).format("ll");
    },

    formatDateTime(dateString) {
      return moment(dateString).format("lll");
    },

    providerForSending(value) {
      const payment_provider = {
        [this.$t(`orders.table.payment_providers.${PAYMENT_PROVIDER.SIMPLE}`)]:
          PAYMENT_PROVIDER.SIMPLE,
        [this.$t(
          `orders.table.payment_providers.${PAYMENT_PROVIDER.SZEP_CARD}`
        )]: PAYMENT_PROVIDER.SZEP_CARD,
      };

      return payment_provider[value] || "";
    },

    handleFilter(hasPage, keydown) {
      if (keydown) {
        this.enterClicked = true;
      }
      this.isLoading = true;
      let salesTo = undefined;
      let salesFrom = undefined;
      let expireTo = undefined;
      let expireFrom = undefined;
      if (this.sales_date) {
        salesTo = this.sales_date.substring(
          this.sales_date.indexOf(" "),
          this.sales_date.length
        );
        salesFrom = this.sales_date.substring(0, this.sales_date.indexOf(" "));
      }
      if (this.expire_date) {
        expireTo = this.expire_date.substring(
          this.expire_date.indexOf(" "),
          this.expire_date.length
        );
        expireFrom = this.expire_date.substring(
          0,
          this.expire_date.indexOf(" ")
        );
      }
      const payload = {
        sales_to: salesTo ? salesTo : null,
        sales_from: salesFrom ? salesFrom : null,
        validity_to: expireTo ? expireTo : null,
        validity_from: expireFrom ? expireFrom : null,
        usage: this.selectedUsage ? this.selectedUsage : null,
        par_id: this.selectedPartner ? this.selectedPartner : null,
        product_id: this.selectedProduct ? this.selectedProduct : null,
        payment_provider: this.payment_provider
          ? this.providerForSending(this.payment_provider)
          : null,
        limit: this.limit,
        page: hasPage ? this.page : 1,
      };
      this.getSalesStatisticApi(payload)
        .then((res) => {
          this.limit = res.limit;
          this.total = res.total;
          this.page = res.page;
          this.list = res.list;
          this.pageSum = res.sum;
          this.isLoading = false;
        })
        .catch((err) => {
          console.log(err);
        });
      setTimeout(() => {
        this.enterClicked = false;
      }, 100);
    },
  },

  computed: {
    ...mapGetters({
      loggedInUser: "users/loggedInUser",
    }),

    isSuperAdmin() {
      return this.loggedInUser && this.loggedInUser.role === 0;
    },

    pageCount() {
      return Math.ceil(this.total / this.limit);
    },
    displayRange() {
      const start = (this.page - 1) * this.limit + 1;
      const end = Math.min(start + this.limit - 1, this.total);
      return `${start}-${end} of ${this.total}`;
    },
    labelForSalesFlatpickr() {
      return (
        !this.hiddenSalesDateLabel &&
        (this.sales_date === "" || this.sales_date === null)
      );
    },
    labelForExpireFlatpickr() {
      return (
        !this.hiddenExpireDateLabel &&
        (this.expire_date === "" || this.expire_date === null)
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.page-sum {
  margin-top: 16px;
  min-width: 100%;
  display: flex;
  justify-content: end;
  padding-right: 8px;
  font-size: 14px;
  font-weight: 600;
}
</style>
